import { Checkbox, cn } from "@nexus/ui";
import type { TFunction } from "i18next";
import type { Dispatch, SetStateAction } from "react";

import { downloadOrderScriptAsset } from "@/features/marketplace/api/marketplace.api";

import type { OrdersPanelTarget } from "../../lib/orders-detail-panel";
import {
  toggleOrderSelection,
  toggleScriptSelection,
  type OrdersGroup,
  type OrdersListQuery,
  type ScriptSelection,
} from "../../lib/orders-list-model";
import type { MarketplaceOrder } from "../../types/marketplace.types";
import { OrderRow } from "./OrderRow";
import {
  ORDERS_CELL_DUE_CLASS,
  ORDERS_CELL_SCRIPTS_CLASS,
  ORDERS_CELL_SENT_CLASS,
  ORDERS_CELL_STATUS_CLASS,
  ORDERS_CELL_TYPE_CLASS,
  ORDERS_CHECKBOX_HIT_TARGET_CLASS,
  ORDERS_GRID_CLASS,
  ORDERS_ROW_PADDING_CLASS,
} from "./orders-list-grid";
import { SortableColumnHeader } from "./SortableColumnHeader";
import { VendorIdentity } from "./VendorIdentity";

export function OrdersListGroups({
  t,
  chrome = "marketplace",
  query,
  setQuery,
  canInteract,
  allVisibleSelected,
  selectedVisibleCount,
  toggleAllVisible,
  groups,
  groupLabel,
  todayYmd,
  expandedIds,
  resolvedPanel,
  treeRowIds,
  rovingActiveId,
  selection,
  setSelection,
  pendingScriptIds,
  retryOrder,
  tenantId,
  variant,
  toggleExpanded,
  setActiveRowId,
  handleScriptStatusChange,
  onViewOrderHistory,
  openPanel,
}: {
  t: TFunction;
  chrome?: "marketplace" | "hub";
  query: OrdersListQuery;
  setQuery: (next: OrdersListQuery) => void;
  canInteract: boolean;
  allVisibleSelected: boolean;
  selectedVisibleCount: number;
  toggleAllVisible: () => void;
  groups: OrdersGroup[];
  groupLabel: (key: string) => string;
  todayYmd: string;
  expandedIds: ReadonlySet<string>;
  resolvedPanel: OrdersPanelTarget | null;
  treeRowIds: string[];
  rovingActiveId: string | null;
  selection: ScriptSelection;
  setSelection: Dispatch<SetStateAction<ScriptSelection>>;
  pendingScriptIds: ReadonlySet<string>;
  retryOrder: {
    isPending: boolean;
    variables?: { orderId: string };
    mutate: (args: { orderId: string; tenantId: string }) => void;
  };
  tenantId: string;
  variant: "in-production" | "history" | "archive";
  toggleExpanded: (orderId: string) => void;
  setActiveRowId: (id: string) => void;
  handleScriptStatusChange: (
    order: MarketplaceOrder,
    scriptId: string,
    next: import("@nexus/contracts").MarketplaceFulfillmentStatus,
  ) => void;
  onViewOrderHistory: (orderId: string) => void;
  openPanel: (target: OrdersPanelTarget | null) => void;
}) {
  return (
    <div
      role="treegrid"
      aria-label={t("marketplace.orders.list.treegridLabel")}
      aria-rowcount={treeRowIds.length + 1}
      aria-colcount={9}
      className="min-w-full overflow-hidden rounded-md border border-border bg-card"
    >
      <div
        role="row"
        aria-rowindex={1}
        className={cn(
          ORDERS_GRID_CLASS,
          ORDERS_ROW_PADDING_CLASS,
          "h-8 border-b border-border bg-muted/30 text-[11px] font-medium uppercase tracking-wide text-muted-foreground",
        )}
      >
        <span role="columnheader" className="flex items-center">
          {canInteract ? (
            <Checkbox
              aria-label={t("marketplace.orders.list.selectAll")}
              checked={
                allVisibleSelected
                  ? true
                  : selectedVisibleCount > 0
                    ? "indeterminate"
                    : false
              }
              onCheckedChange={toggleAllVisible}
              className={cn(
                ORDERS_CHECKBOX_HIT_TARGET_CLASS,
                "data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground",
              )}
            />
          ) : null}
        </span>
        <span role="columnheader" />
        <SortableColumnHeader
          label={t("marketplace.orders.list.columns.vendor")}
          sortBy="vendor"
          activeSortBy={query.sortBy}
          sortDirection={query.sortDirection}
          onChange={(sortBy, sortDirection) =>
            setQuery({ ...query, sortBy, sortDirection })
          }
        />
        <span role="columnheader" className={ORDERS_CELL_TYPE_CLASS}>
          {t("marketplace.orders.list.columns.type", "Type")}
        </span>
        <SortableColumnHeader
          label={t("marketplace.orders.list.columns.scripts")}
          sortBy="scripts"
          activeSortBy={query.sortBy}
          sortDirection={query.sortDirection}
          onChange={(sortBy, sortDirection) =>
            setQuery({ ...query, sortBy, sortDirection })
          }
          className={ORDERS_CELL_SCRIPTS_CLASS}
        />
        <SortableColumnHeader
          label={t("marketplace.orders.list.columns.status")}
          sortBy="status"
          activeSortBy={query.sortBy}
          sortDirection={query.sortDirection}
          onChange={(sortBy, sortDirection) =>
            setQuery({ ...query, sortBy, sortDirection })
          }
          className={ORDERS_CELL_STATUS_CLASS}
        />
        <SortableColumnHeader
          label={t("marketplace.orders.list.columns.sent")}
          sortBy="createdAt"
          activeSortBy={query.sortBy}
          sortDirection={query.sortDirection}
          defaultDirection="desc"
          onChange={(sortBy, sortDirection) =>
            setQuery({ ...query, sortBy, sortDirection })
          }
          className={ORDERS_CELL_SENT_CLASS}
        />
        <SortableColumnHeader
          label={t("marketplace.orders.list.columns.due")}
          sortBy="deadline"
          activeSortBy={query.sortBy}
          sortDirection={query.sortDirection}
          onChange={(sortBy, sortDirection) =>
            setQuery({ ...query, sortBy, sortDirection })
          }
          className={ORDERS_CELL_DUE_CLASS}
        />
        <span role="columnheader" />
      </div>
      {groups.map((group) => (
        <section
          key={group.key}
          role="rowgroup"
          aria-label={
            query.groupBy === "none" ? undefined : groupLabel(group.key)
          }
        >
          {query.groupBy !== "none" ? (
            <h3
              role="presentation"
              data-orders-group-chrome={chrome}
              className={cn(
                chrome === "hub"
                  ? "flex items-center gap-2 bg-muted/60 px-4 py-3 text-sm font-medium text-foreground"
                  : cn(
                      ORDERS_ROW_PADDING_CLASS,
                      "flex h-8 items-center gap-2 border-b border-border-subtle bg-muted/15 pl-[4.25rem] text-xs font-medium text-foreground",
                    ),
              )}
            >
              {query.groupBy === "vendor" ? (
                <VendorIdentity
                  name={groupLabel(group.key)}
                  logoUrl={group.orders[0]?.vendor.logoUrl ?? null}
                  className="size-5 text-[9px]"
                />
              ) : null}
              {groupLabel(group.key)}
              <span
                className={cn(
                  "text-muted-foreground",
                  chrome === "hub" ? "text-[11px] tabular-nums" : "tabular-nums",
                )}
              >
                {group.orders.length}
              </span>
            </h3>
          ) : null}
          <ul role="presentation" className="divide-y divide-border-subtle">
            {group.orders.map((order) => (
              <OrderRow
                key={order.id}
                chrome={chrome}
                order={order}
                tenantId={tenantId}
                variant={variant}
                todayYmd={todayYmd}
                canEdit={canInteract}
                expanded={expandedIds.has(order.id)}
                panelOpen={resolvedPanel != null}
                panelSelected={
                  resolvedPanel?.kind === "order" &&
                  resolvedPanel.orderId === order.id
                }
                panelScriptId={
                  resolvedPanel?.kind === "script"
                    ? resolvedPanel.scriptId
                    : null
                }
                tabIndex={`order:${order.id}` === rovingActiveId ? 0 : -1}
                rowIndex={treeRowIds.indexOf(`order:${order.id}`) + 2}
                activeRowId={rovingActiveId}
                density={query.density}
                selection={selection}
                pendingScriptIds={pendingScriptIds}
                retryPending={
                  retryOrder.isPending &&
                  retryOrder.variables?.orderId === order.id
                }
                onToggleExpanded={() => toggleExpanded(order.id)}
                onFocusTreeRow={setActiveRowId}
                onToggleOrderSelected={() =>
                  setSelection((current) =>
                    toggleOrderSelection(order, current),
                  )
                }
                onToggleScriptSelected={(scriptId) =>
                  setSelection((current) =>
                    toggleScriptSelection(scriptId, current),
                  )
                }
                onScriptStatusChange={(scriptId, next) =>
                  handleScriptStatusChange(order, scriptId, next)
                }
                onRetryHandoff={() =>
                  retryOrder.mutate({ orderId: order.id, tenantId })
                }
                onViewHistory={() => onViewOrderHistory(order.id)}
                onOpenOrderDetails={() =>
                  openPanel({ kind: "order", orderId: order.id })
                }
                onOpenScriptDetails={(script) =>
                  openPanel({
                    kind: "script",
                    orderId: order.id,
                    scriptId: script.id,
                  })
                }
                onDownloadAsset={(script) =>
                  downloadOrderScriptAsset(order.id, script.id, tenantId)
                }
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
